import { useEffect, useRef, useState } from "react";

const ACTION_W = 80;
const TONES = {
  edit: "bg-hocp-deepTeal text-white",
  primary: "bg-hocp-mint text-hocp-deepTeal",
  danger: "bg-[#DC2626] text-white",
};

/**
 * SwipeableCard — swipe left on touch devices to reveal action buttons (Edit / Check-in / Cancel…).
 * actions = [{ key, label, icon, tone: "edit"|"primary"|"danger", onClick, testid }]
 * Only one card stays open at a time (cards broadcast `hocp:swipe-open`).
 */
export function SwipeableCard({ children, actions = [], testid, className = "" }) {
  const [dx, setDxState] = useState(0);
  const dxRef = useRef(0);
  const setDx = (v) => { dxRef.current = v; setDxState(v); };
  const [open, setOpen] = useState(false);
  const [dragging, setDragging] = useState(false);
  const start = useRef(null);
  const id = useRef(Symbol("swipe"));
  const max = actions.length * ACTION_W;

  useEffect(() => {
    const onOther = (e) => { if (e.detail !== id.current) { setOpen(false); setDx(0); } };
    window.addEventListener("hocp:swipe-open", onOther);
    return () => window.removeEventListener("hocp:swipe-open", onOther);
  }, []);

  const onTouchStart = (e) => { start.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, base: open ? -max : 0 }; setDragging(true); };
  const onTouchMove = (e) => {
    if (!start.current || !actions.length) return;
    const mx = e.touches[0].clientX - start.current.x, my = e.touches[0].clientY - start.current.y;
    if (Math.abs(my) > Math.abs(mx)) return;
    setDx(Math.min(0, Math.max(-max, start.current.base + mx)));
  };
  const onTouchEnd = () => {
    if (!start.current) return;
    const willOpen = dxRef.current < -max / 2;
    setOpen(willOpen); setDx(willOpen ? -max : 0); start.current = null; setDragging(false);
    if (willOpen) window.dispatchEvent(new CustomEvent("hocp:swipe-open", { detail: id.current }));
  };
  const close = () => { setOpen(false); setDx(0); };

  return (
    <div className={`relative overflow-hidden rounded-[8px] ${className}`} data-testid={testid} data-swipe-open={open ? "true" : "false"}>
      {actions.length > 0 && (
        <div className="absolute inset-y-0 right-0 flex on-dark" style={{ width: max }} aria-hidden={!open} data-testid={testid ? `${testid}-actions` : undefined}>
          {actions.map((a) => {
            const Icon = a.icon;
            return (
              <button key={a.key} type="button" onClick={() => { close(); a.onClick?.(); }} data-testid={a.testid}
                className={`flex flex-col items-center justify-center gap-1 text-[11px] font-semibold ${TONES[a.tone] || TONES.edit}`} style={{ width: ACTION_W }}>
                {Icon && <Icon size={20} strokeWidth={1.5} />}{a.label}
              </button>
            );
          })}
        </div>
      )}
      <div onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd} onClick={open ? close : undefined}
        className={`relative bg-white ${dragging ? "" : "transition-transform duration-200 ease-out"}`} style={{ transform: `translateX(${dx}px)` }}
        data-testid={testid ? `${testid}-surface` : undefined}>
        {children}
      </div>
    </div>
  );
}
